import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { call } from '@/api/desk';
import { DataTable, FormModal, Link2, Modal, Notice, PageHeader, Sel, Tabs, arr, dOnly, errMsg, useAction, useGet } from '@/features/shared/ops/kit';
import { ResourcePanel } from '@/features/shared/ops/crud';
import { badge, useLevelOptions, useStaffOptions, useSubjectOptions } from '../lib';

const num = (v: any) => (v === '' || v == null ? null : Number(v));
const SECTIONS = ['nursery', 'primary', 'junior_secondary', 'senior_secondary'].map((v) => ({ value: v, label: v.replace('_', ' ') }));

/** Choose which subjects a class takes. The API replaces the whole list on save. */
function ClassSubjectsModal({ cls, onClose, onDone }: { cls: any; onClose: () => void; onDone: () => void }) {
  const detail = useGet<any>('desk-class-detail', `/school/classes/${cls.id}`);
  const { subjects, isLoading } = useSubjectOptions();
  const [sel, setSel] = useState<Record<number, { on: boolean; compulsory: boolean; pass: string }>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!detail.data) return;
    const m: Record<number, { on: boolean; compulsory: boolean; pass: string }> = {};
    arr(detail.data.subjects).forEach((s: any) => { m[s.id] = { on: true, compulsory: !!s.is_compulsory, pass: s.pass_mark != null ? String(s.pass_mark) : '' }; });
    setSel(m);
  }, [detail.data]);

  const upd = (id: number, patch: Partial<{ on: boolean; compulsory: boolean; pass: string }>) =>
    setSel((s) => ({ ...s, [id]: { ...(s[id] ?? { on: false, compulsory: true, pass: '' }), ...patch } }));

  const save = async () => {
    setBusy(true); setErr('');
    try {
      const list = Object.entries(sel).filter(([, v]) => v.on).map(([id, v]) => ({
        subject_id: Number(id), is_compulsory: v.compulsory, ...(v.pass !== '' ? { pass_mark: Number(v.pass) } : {}),
      }));
      await call('post', `/school/classes/${cls.id}/subjects`, { body: { subjects: list } });
      onDone(); onClose();
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Modal wide title={`Subjects — ${cls.name} ${cls.arm ?? ''}`} onClose={onClose}>
      {err && <Notice kind="error">{err}</Notice>}
      {detail.isLoading || isLoading ? <p className="text-sm text-slate-500">Loading…</p> : detail.error ? <Notice kind="error">Could not load class.</Notice> : subjects.length === 0 ? <Notice>Add subjects first (Subjects tab).</Notice> : (
        <div className="max-h-96 overflow-auto space-y-1">
          {subjects.map((s: any) => {
            const v = sel[s.id];
            return (
              <div key={s.id} className="flex items-center gap-3 text-sm py-1 border-b border-slate-100">
                <label className="flex items-center gap-2 flex-1"><input type="checkbox" checked={!!v?.on} onChange={(e) => upd(s.id, { on: e.target.checked })} />{s.name}</label>
                <label className="flex items-center gap-1 text-xs text-slate-600"><input type="checkbox" disabled={!v?.on} checked={v?.compulsory ?? true} onChange={(e) => upd(s.id, { compulsory: e.target.checked })} />Compulsory</label>
                <input type="number" min={0} max={100} placeholder="Pass %" disabled={!v?.on} value={v?.pass ?? ''} onChange={(e) => upd(s.id, { pass: e.target.value })} className="border rounded-lg px-2 py-1 w-20 text-xs" />
              </div>
            );
          })}
        </div>
      )}
      <div className="flex justify-end gap-2 mt-4"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save subjects'}</Button></div>
    </Modal>
  );
}

function Classes() {
  const qc = useQueryClient();
  const { options: levels } = useLevelOptions();
  const { options: teachers } = useStaffOptions('teacher');
  const { options: subjects } = useSubjectOptions();
  const [level, setLevel] = useState('');
  const [subj, setSubj] = useState<any>(null);
  const [assign, setAssign] = useState<any>(null);
  const [assignForm, setAssignForm] = useState({ teacher_id: '', subject_id: '', is_form_teacher: false });
  const [assignErr, setAssignErr] = useState('');
  const refresh = () => qc.invalidateQueries({ queryKey: ['desk-classes'] });
  const fields = (edit: boolean) => [
    ...(edit ? [] : [{ name: 'class_level_id', label: 'Level', type: 'select' as const, options: levels, required: true }]),
    { name: 'name', label: 'Name', type: 'text' as const, required: true, maxLength: 60 },
    { name: 'arm', label: 'Arm (e.g. A)', type: 'text' as const, maxLength: 20 },
    { name: 'form_teacher_id', label: 'Form teacher', type: 'select' as const, options: teachers },
    { name: 'capacity', label: 'Capacity', type: 'number' as const, min: 1, max: 500 },
    ...(edit ? [{ name: 'is_active', label: 'Active', type: 'checkbox' as const }] : []),
  ];
  const clean = (v: any) => ({ ...v, class_level_id: v.class_level_id != null ? Number(v.class_level_id) : undefined, arm: v.arm || null, form_teacher_id: num(v.form_teacher_id), capacity: num(v.capacity) });

  const submitAssign = async () => {
    setAssignErr('');
    try {
      await call('post', `/school/classes/${assign.id}/assign-teacher`, { body: { teacher_id: Number(assignForm.teacher_id), subject_id: num(assignForm.subject_id), is_form_teacher: assignForm.is_form_teacher } });
      setAssign(null); refresh(); qc.invalidateQueries({ queryKey: ['desk-assignments'] });
    } catch (e) { setAssignErr(errMsg(e)); }
  };

  return (
    <>
      <ResourcePanel qkey="desk-classes" title="Classes" url="/school/classes" plain params={{ level_id: level }}
        toolbar={<div className="mb-3"><Sel value={level} onChange={setLevel} options={levels} placeholder="All levels" /></div>}
        cols={[
          { head: 'Class', cell: (r) => `${r.name} ${r.arm ?? ''}`.trim() },
          { head: 'Level', cell: (r) => r.class_level?.name ?? r.classLevel?.name ?? '—' },
          { head: 'Students', cell: (r) => `${r.active_students_count ?? 0}${r.capacity ? ` / ${r.capacity}` : ''}`, right: true },
          { head: 'Status', cell: (r) => badge(r.is_active ? 'active' : 'inactive') },
        ]}
        createLabel="Add class" createFields={fields(false)} mapCreate={clean}
        editFields={fields(true)} editUrl={(r) => `/school/classes/${r.id}`} mapEdit={clean}
        deleteUrl={(r) => `/school/classes/${r.id}`}
        rowActions={(r) => (
          <>
            <Link2 onClick={() => setSubj(r)}>Subjects</Link2>
            <Link2 onClick={() => { setAssignForm({ teacher_id: '', subject_id: '', is_form_teacher: false }); setAssignErr(''); setAssign(r); }}>Assign teacher</Link2>
          </>
        )} />
      {subj && <ClassSubjectsModal cls={subj} onClose={() => setSubj(null)} onDone={refresh} />}
      {assign && (
        <Modal title={`Assign teacher — ${assign.name} ${assign.arm ?? ''}`} onClose={() => setAssign(null)}>
          {assignErr && <Notice kind="error">{assignErr}</Notice>}
          <div className="space-y-3">
            <Sel value={assignForm.teacher_id} onChange={(v) => setAssignForm({ ...assignForm, teacher_id: v })} options={teachers} placeholder="Select teacher" />
            <Sel value={assignForm.subject_id} onChange={(v) => setAssignForm({ ...assignForm, subject_id: v })} options={subjects} placeholder="All subjects / none" />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={assignForm.is_form_teacher} onChange={(e) => setAssignForm({ ...assignForm, is_form_teacher: e.target.checked })} />Make form teacher</label>
            <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setAssign(null)}>Cancel</Button><Button disabled={!assignForm.teacher_id} onClick={submitAssign}>Assign</Button></div>
          </div>
        </Modal>
      )}
    </>
  );
}

function Levels() {
  return (
    <ResourcePanel qkey="desk-levels" title="Class levels" url="/school/class-levels" plain
      cols={[{ head: 'Position', cell: (r) => r.position }, { head: 'Name', cell: (r) => r.name }, { head: 'Section', cell: (r) => String(r.section ?? '').replace('_', ' ') }]}
      createLabel="Add level"
      createFields={[
        { name: 'name', label: 'Name', type: 'text', required: true, maxLength: 60 },
        { name: 'section', label: 'Section', type: 'select', options: SECTIONS, required: true },
        { name: 'position', label: 'Position (order)', type: 'number', min: 1, max: 99, required: true },
      ]}
      mapCreate={(v) => ({ ...v, position: Number(v.position) })} />
  );
}

function Subjects() {
  const fields = [
    { name: 'name', label: 'Name', type: 'text' as const, required: true, maxLength: 80 },
    { name: 'code', label: 'Code', type: 'text' as const, maxLength: 12 },
    { name: 'category', label: 'Category', type: 'select' as const, options: ['core', 'elective', 'extra'].map((v) => ({ value: v, label: v })) },
  ];
  return (
    <ResourcePanel qkey="desk-subjects" title="Subjects" url="/school/subjects" plain
      cols={[{ head: 'Name', cell: (r) => r.name }, { head: 'Code', cell: (r) => r.code ?? '—' }, { head: 'Category', cell: (r) => r.category ?? '—' }, { head: 'Status', cell: (r) => badge(r.is_active === false ? 'inactive' : 'active') }]}
      createLabel="Add subject" createFields={fields} createInitial={{ category: 'core' }} mapCreate={(v) => ({ ...v, code: v.code || null })}
      editFields={[...fields, { name: 'is_active', label: 'Active', type: 'checkbox' as const }]} editUrl={(r) => `/school/subjects/${r.id}`} mapEdit={(v) => ({ ...v, code: v.code || null })}
      deleteUrl={(r) => `/school/subjects/${r.id}`} />
  );
}

function Assignments() {
  const [cls, setCls] = useState('');
  const [teacher, setTeacher] = useState('');
  const { options: teachers } = useStaffOptions('teacher');
  const { options: subjects } = useSubjectOptions();
  const classes = useGet<any>('desk-asg-classes', '/school/classes');
  const classOpts = arr(classes.data).map((c: any) => ({ value: c.id, label: `${c.name} ${c.arm ?? ''}`.trim() }));
  return (
    <ResourcePanel qkey="desk-assignments" title="Teacher assignments" url="/school/teacher-assignments" plain params={{ class_id: cls, teacher_id: teacher }}
      toolbar={<div className="flex gap-3 mb-3"><Sel value={cls} onChange={setCls} options={classOpts} placeholder="All classes" /><Sel value={teacher} onChange={setTeacher} options={teachers} placeholder="All teachers" /></div>}
      cols={[
        { head: 'Teacher', cell: (r) => r.teacher },
        { head: 'Class', cell: (r) => `${r.class} ${r.arm ?? ''}`.trim() },
        { head: 'Subject', cell: (r) => r.subject ?? 'Whole class' },
        { head: 'Form teacher', cell: (r) => (r.is_form_teacher ? 'Yes' : '') },
      ]}
      createLabel="Assign teacher"
      createFields={[
        { name: 'teacher_id', label: 'Teacher', type: 'select', options: teachers, required: true },
        { name: 'school_class_id', label: 'Class', type: 'select', options: classOpts, required: true },
        { name: 'subject_id', label: 'Subject (optional)', type: 'select', options: subjects },
        { name: 'is_form_teacher', label: 'Form teacher', type: 'checkbox' },
      ]}
      mapCreate={(v) => ({ teacher_id: Number(v.teacher_id), school_class_id: Number(v.school_class_id), subject_id: num(v.subject_id), is_form_teacher: !!v.is_form_teacher })}
      deleteUrl={(r) => `/school/teacher-assignments/${r.id}`} />
  );
}

function Sessions() {
  const q = useGet<any>('desk-sessions', '/school/sessions');
  const [sess, setSess] = useState('');
  const terms = useGet<any>('desk-terms', '/school/terms', { session_id: sess || undefined });
  const a = useAction(() => { q.refetch(); terms.refetch(); });
  const sessions = arr(q.data);
  const sessOpts = sessions.map((s: any) => ({ value: s.id, label: s.name }));
  const dateFields = [{ name: 'starts_on', label: 'Starts', type: 'date' as const, required: true }, { name: 'ends_on', label: 'Ends', type: 'date' as const, required: true }];
  const [form, setForm] = useState<null | { kind: 'session' | 'term'; row?: any }>(null);
  const f = form;

  return (
    <div className="space-y-4">
      {a.banner}
      <Card title="Academic sessions" action={<Button onClick={() => setForm({ kind: 'session' })}>Add session</Button>}>
        <DataTable loading={q.isLoading} error={q.error} rows={sessions} empty="No sessions yet." cols={[
          { head: 'Session', cell: (r) => r.name },
          { head: 'Dates', cell: (r) => `${dOnly(r.starts_on)} – ${dOnly(r.ends_on)}` },
          { head: 'Status', cell: (r) => badge(r.is_current ? 'current' : r.is_closed || r.status === 'closed' ? 'closed' : 'inactive') },
          {
            head: 'Actions', cell: (r) => (
              <span className="flex gap-3">
                <Link2 onClick={() => setForm({ kind: 'session', row: r })}>Edit</Link2>
                {!r.is_current && <Link2 onClick={() => a.run(() => call('post', `/school/sessions/${r.id}/activate`), { confirm: 'Make this the current session? The previous one will be closed.', ok: 'Session activated.' })}>Activate</Link2>}
                <Link2 onClick={() => a.run(() => call('post', `/school/sessions/${r.id}/close`), { confirm: 'Close this session?', ok: 'Session closed.' })}>Close</Link2>
              </span>
            ),
          },
        ]} />
      </Card>
      <Card title="Terms" action={<Button onClick={() => setForm({ kind: 'term' })}>Add term</Button>}>
        <div className="mb-3"><Sel value={sess} onChange={setSess} options={sessOpts} placeholder="All sessions" /></div>
        <DataTable loading={terms.isLoading} error={terms.error} rows={arr(terms.data)} empty="No terms." cols={[
          { head: 'Term', cell: (r) => r.name },
          { head: 'Session', cell: (r) => sessions.find((s: any) => s.id === r.academic_session_id)?.name ?? r.academic_session_id },
          { head: 'Dates', cell: (r) => `${dOnly(r.starts_on)} – ${dOnly(r.ends_on)}` },
          { head: 'Next term', cell: (r) => (r.next_term_begins ? dOnly(r.next_term_begins) : '—') },
          { head: 'Status', cell: (r) => badge(r.is_current ? 'current' : 'inactive') },
          {
            head: 'Actions', cell: (r) => (
              <span className="flex gap-3">
                <Link2 onClick={() => setForm({ kind: 'term', row: r })}>Edit</Link2>
                {!r.is_current && <Link2 onClick={() => a.run(() => call('post', `/school/terms/${r.id}/activate`), { confirm: 'Make this the current term?', ok: 'Term activated.' })}>Activate</Link2>}
              </span>
            ),
          },
        ]} />
      </Card>
      {f?.kind === 'session' && <FormLazy {...{ title: f.row ? 'Edit session' : 'Add session', onClose: () => setForm(null), initial: f.row ? { ...f.row, starts_on: String(f.row.starts_on).slice(0, 10), ends_on: String(f.row.ends_on).slice(0, 10) } : {}, fields: [{ name: 'name', label: 'Name (e.g. 2025/2026)', type: 'text' as const, required: true, maxLength: 40 }, ...dateFields], submit: async (v: any) => { await call(f.row ? 'put' : 'post', f.row ? `/school/sessions/${f.row.id}` : '/school/sessions', { body: v }); q.refetch(); } }} />}
      {f?.kind === 'term' && <FormLazy {...{
        title: f.row ? 'Edit term' : 'Add term', onClose: () => setForm(null),
        initial: f.row ? { ...f.row, starts_on: String(f.row.starts_on).slice(0, 10), ends_on: String(f.row.ends_on).slice(0, 10), next_term_begins: f.row.next_term_begins ? String(f.row.next_term_begins).slice(0, 10) : '' } : { academic_session_id: sess },
        fields: [
          ...(f.row ? [] : [{ name: 'academic_session_id', label: 'Session', type: 'select' as const, options: sessOpts, required: true }, { name: 'position', label: 'Position (1-4)', type: 'number' as const, min: 1, max: 4, required: true }]),
          { name: 'name', label: 'Name (e.g. First Term)', type: 'text' as const, required: true, maxLength: 40 }, ...dateFields,
          { name: 'next_term_begins', label: 'Next term begins', type: 'date' as const },
        ],
        submit: async (v: any) => {
          const body = { ...v, next_term_begins: v.next_term_begins || null };
          if (!f.row) { body.academic_session_id = Number(v.academic_session_id); body.position = Number(v.position); }
          await call(f.row ? 'put' : 'post', f.row ? `/school/terms/${f.row.id}` : '/school/terms', { body });
          terms.refetch();
        },
      }} />}
    </div>
  );
}

function FormLazy(p: { title: string; onClose: () => void; initial: Record<string, any>; fields: any[]; submit: (v: any) => Promise<unknown> }) {
  return <FormModal title={p.title} fields={p.fields} initial={p.initial} onClose={p.onClose} onSubmit={p.submit} />;
}

export function ClassesPage() {
  const [tab, setTab] = useState('classes');
  return (
    <div>
      <PageHeader title="Classes & subjects" sub="Classes, levels, subjects, teachers and the academic calendar" />
      <Tabs value={tab} onChange={setTab} tabs={[{ id: 'classes', label: 'Classes' }, { id: 'levels', label: 'Levels' }, { id: 'subjects', label: 'Subjects' }, { id: 'teachers', label: 'Teachers' }, { id: 'sessions', label: 'Sessions & terms' }]} />
      {tab === 'classes' && <Classes />}
      {tab === 'levels' && <Levels />}
      {tab === 'subjects' && <Subjects />}
      {tab === 'teachers' && <Assignments />}
      {tab === 'sessions' && <Sessions />}
    </div>
  );
}
